/**
 * Paddle.js loader + checkout helper.
 *
 * Loads the Paddle.js v2 script on demand, initializes it once with the public
 * client-side token (VITE_PADDLE_CLIENT_TOKEN), and opens overlay checkouts.
 * The Paddle environment (sandbox/production) is driven by VITE_PADDLE_ENVIRONMENT.
 */

interface PaddleCheckoutItem {
  priceId: string
  quantity: number
}

export interface PaddleEvent {
  name?: string
  [key: string]: unknown
}

interface PaddleGlobal {
  Environment: { set(env: string): void }
  Initialize(opts: { token: string; eventCallback?: (event: PaddleEvent) => void }): void
  Checkout: { open(opts: Record<string, unknown>): void; close(): void }
}

declare global {
  interface Window {
    Paddle?: PaddleGlobal
  }
}

const PADDLE_JS_URL = 'https://cdn.paddle.com/paddle/v2/paddle.js'

let initialized = false
let loadPromise: Promise<void> | null = null
// The latest checkout's completion handler — Paddle's eventCallback is global,
// so we dispatch to whichever checkout was opened most recently.
let onCheckoutComplete: (() => void) | null = null
/**
 * Whether the overlay on screen is one openCheckout() opened. Only those are
 * closed on completion: the caller shows its own "Payment successful" dialog,
 * and Paddle's success screen left on top of it made the buyer close two
 * success screens. A payment link keeps Paddle's — outside Lark it is the page.
 */
let ownCheckoutOpen = false

/**
 * Listeners for checkout events we did not initiate.
 *
 * A Paddle payment link (`?_ptxn=...`) makes Paddle.js open its own overlay
 * from inside Initialize(), so there is no openCheckout() call to hang a
 * completion handler off. eventCallback is global and can only be supplied
 * once, at Initialize() time, so anything else needing these events has to
 * come through here.
 */
const eventListeners = new Set<(event: PaddleEvent) => void>()

/** Subscribe to raw Paddle checkout events. Returns an unsubscribe function. */
export function onPaddleEvent(listener: (event: PaddleEvent) => void): () => void {
  eventListeners.add(listener)
  return () => {
    eventListeners.delete(listener)
  }
}

function loadScript(): Promise<void> {
  if (loadPromise) return loadPromise
  loadPromise = new Promise<void>((resolve, reject) => {
    if (window.Paddle) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = PADDLE_JS_URL
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Paddle.js'))
    document.head.appendChild(script)
  }).catch((error) => {
    // Drop the cached promise on failure. Caching a rejected one would turn a
    // single CDN blip into "checkout is broken until you reload the page" —
    // more likely now that preload() runs this on page load.
    loadPromise = null
    throw error
  })
  return loadPromise
}

export interface OpenCheckoutOptions {
  /** Called when the customer completes payment in the overlay. */
  onComplete?: () => void
}

export function usePaddle() {
  async function ensureReady(): Promise<PaddleGlobal> {
    await loadScript()
    const paddle = window.Paddle
    if (!paddle) throw new Error('Paddle.js is unavailable')
    if (!initialized) {
      // Build-time value: empty in an env whose Paddle catalog isn't wired up
      // yet. Initializing with an empty token yields an overlay that fails
      // inside a cross-origin iframe, where the error is invisible.
      if (!import.meta.env.VITE_PADDLE_CLIENT_TOKEN) {
        throw new Error('VITE_PADDLE_CLIENT_TOKEN is not configured for this environment')
      }
      if (import.meta.env.VITE_PADDLE_ENVIRONMENT === 'sandbox') {
        paddle.Environment.set('sandbox')
      }
      paddle.Initialize({
        token: import.meta.env.VITE_PADDLE_CLIENT_TOKEN,
        eventCallback: (event) => {
          // The checkout overlay is a cross-origin iframe, so its internal
          // errors never reach our console / in-app DebugConsole. Paddle's
          // eventCallback is the only channel out — mirror every event to the
          // console so failures are actually visible while debugging.
          const name = event?.name ?? 'unknown'
          if (name === 'checkout.error') {
            console.error('[Paddle]', name, event)
          } else {
            console.log('[Paddle]', name, event)
          }
          if (name === 'checkout.completed') {
            if (ownCheckoutOpen) {
              ownCheckoutOpen = false
              paddle.Checkout.close()
            }
            onCheckoutComplete?.()
          }
          if (name === 'checkout.closed') ownCheckoutOpen = false
          // Snapshot first: a listener that unsubscribes itself on completion
          // would otherwise mutate the set mid-iteration.
          for (const listener of [...eventListeners]) listener(event)
        },
      })
      initialized = true
    }
    return paddle
  }

  /**
   * Open a Paddle overlay checkout.
   * @param items     priced line items (Pro plan, optional Template Packs)
   * @param customData attached to the resulting subscription — must include
   *                   `checkout_token`, minted server-side via
   *                   `tenantApi.createCheckoutToken()`. This runs in the
   *                   browser, so the webhook never trusts a raw tenant key
   *                   from here (see worker utils/checkoutToken.ts).
   */
  async function openCheckout(
    items: PaddleCheckoutItem[],
    customData: Record<string, string>,
    options: OpenCheckoutOptions = {},
  ): Promise<void> {
    const paddle = await ensureReady()
    onCheckoutComplete = options.onComplete ?? null
    ownCheckoutOpen = true
    paddle.Checkout.open({
      items: items.map((item) => ({ priceId: item.priceId, quantity: item.quantity })),
      customData,
      settings: {
        displayMode: 'overlay',
        theme: 'light',
        allowLogout: false,
      },
    })
  }

  /**
   * Open a checkout for a transaction that already exists in Paddle.
   *
   * Distinct from openCheckout: a payment link's transaction already carries
   * its own items, customer and custom_data, so supplying items here would be
   * wrong — and there is no checkout token to attach, because the transaction
   * was bound to its tenant when Paddle created it.
   */
  async function openTransaction(transactionId: string): Promise<void> {
    const paddle = await ensureReady()
    // Deliberately does NOT touch `onCheckoutComplete`. That handler belongs to
    // whichever openCheckout() call set it, and clearing it here would silently
    // disable the post-purchase refresh for a signed-in customer. Completion of
    // a payment link is observed through onPaddleEvent instead.
    ownCheckoutOpen = false
    paddle.Checkout.open({
      transactionId,
      settings: {
        displayMode: 'overlay',
        theme: 'light',
        allowLogout: false,
      },
    })
  }

  /**
   * Warm up Paddle.js ahead of the first click.
   *
   * The script is ~100s of ms to fetch and initialize, and that cost otherwise
   * lands between the click and the overlay appearing, where it reads as an
   * unresponsive button. Call this when a page with buy buttons mounts.
   *
   * Deliberately swallows errors: this is an optimisation, and a failure here
   * must not surface. openCheckout() runs the same path and reports properly.
   */
  async function preload(): Promise<void> {
    try {
      await ensureReady()
    } catch {
      // Ignored — see above.
    }
  }

  return { openCheckout, openTransaction, preload }
}
